import { BACKEND_URL } from "../../lib/constants";

const CODE_EXAMPLES = {
  python: (apiUrl) => `import os
import requests

API_KEY = os.environ["QAIRO_API_KEY"]  # Set your key as env var
API_URL = "${apiUrl}/api/v1"

# Generate text content
response = requests.post(
    f"{API_URL}/generate/text",
    headers={"X-API-Key": API_KEY},
    json={
        "content_type": "blog",
        "topic": "Benefits of AI in marketing",
        "tone": "professional",
        "language": "en"
    }
)
print(response.json()["content"])

# Generate image
response = requests.post(
    f"{API_URL}/generate/image",
    headers={"X-API-Key": API_KEY},
    json={
        "prompt": "Modern office workspace",
        "style": "professional"
    }
)
# response.json()["image_data"] contains base64 image`,
  javascript: (apiUrl) => `const API_KEY = process.env.QAIRO_API_KEY;  // Set your key as env var
const API_URL = "${apiUrl}/api/v1";

// Generate text content
const textRes = await fetch(\`\${API_URL}/generate/text\`, {
  method: "POST",
  headers: {
    "X-API-Key": API_KEY,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    content_type: "blog",
    topic: "Benefits of AI in marketing",
    tone: "professional",
    language: "en"
  })
});
const { content } = await textRes.json();
console.log(content);

// Generate image
const imgRes = await fetch(\`\${API_URL}/generate/image\`, {
  method: "POST",
  headers: {
    "X-API-Key": API_KEY,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    prompt: "Modern office workspace",
    style: "professional"
  })
});
const { image_data } = await imgRes.json();`,
  curl: (apiUrl) => `# Set your API key
export QAIRO_API_KEY="your_key_here"

# Generate text content
curl -X POST "${apiUrl}/api/v1/generate/text" \\
  -H "X-API-Key: $QAIRO_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"content_type":"blog","topic":"Benefits of AI","tone":"professional"}'

# Generate image
curl -X POST "${apiUrl}/api/v1/generate/image" \\
  -H "X-API-Key: $QAIRO_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt":"Modern office","style":"professional"}'

# Check account
curl "${apiUrl}/api/v1/me" \\
  -H "X-API-Key: $QAIRO_API_KEY"`,
};

export function ApiDocumentation({ activeTab, setActiveTab }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="api-docs-section">
      <h2 className="text-lg font-bold mb-4">API Documentation</h2>

      <div className="mb-6">
        <h3 className="font-bold mb-2">Base URL</h3>
        <code className="bg-gray-100 px-3 py-2 text-sm rounded block" data-testid="api-base-url">
          {BACKEND_URL}/api/v1
        </code>
      </div>

      <div className="mb-6">
        <h3 className="font-bold mb-2">Authentication</h3>
        <p className="text-sm text-gray-600 mb-2">
          Include your API key in the{" "}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">X-API-Key</code> header with every request.
        </p>
      </div>

      <div className="mb-6">
        <h3 className="font-bold mb-3">Endpoints</h3>
        <div className="space-y-3">
          <div className="border border-gray-100 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">POST</span>
              <code className="text-sm font-bold">/generate/text</code>
            </div>
            <p className="text-sm text-gray-600">Generate text content. Costs 1 credit.</p>
            <p className="text-xs text-gray-400 mt-1">
              Body: content_type, topic, tone, language, template_id, template_variables
            </p>
          </div>
          <div className="border border-gray-100 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">POST</span>
              <code className="text-sm font-bold">/generate/image</code>
            </div>
            <p className="text-sm text-gray-600">Generate images. Costs 2 credits. Returns base64.</p>
            <p className="text-xs text-gray-400 mt-1">Body: prompt, style</p>
          </div>
          <div className="border border-gray-100 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded">GET</span>
              <code className="text-sm font-bold">/me</code>
            </div>
            <p className="text-sm text-gray-600">Check account info and remaining credits.</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-bold mb-3">Code Examples</h3>
        <div className="flex gap-2 mb-4">
          {["python", "javascript", "curl"].map((lang) => (
            <button
              key={lang}
              onClick={() => setActiveTab(lang)}
              className={`px-4 py-2 text-sm font-medium rounded-lg ${
                activeTab === lang ? "bg-black text-white" : "bg-gray-100 hover:bg-gray-200"
              }`}
              data-testid={`code-tab-${lang}`}
            >
              {lang.charAt(0).toUpperCase() + lang.slice(1)}
            </button>
          ))}
        </div>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm" data-testid="code-example">
          <code>{CODE_EXAMPLES[activeTab](BACKEND_URL)}</code>
        </pre>
      </div>
    </div>
  );
}
